/**
 * The 404 inside the app shell — the sidebar stays, so nobody is stranded.
 *
 * A wrong URL in Firmio is almost always a link to something that moved or
 * was deleted (a project link in an old email, a task key that was renamed,
 * a bookmark from before a module was reorganised), so the page reads the
 * first path segment and says which part of the app the link was pointing
 * at, with a button straight back to that section's list. Sections the
 * caller may not see are not offered — a 404 must not become a 403.
 *
 * What it deliberately does not do: guess at records. Whether a specific
 * project or task exists is the detail page's job (those render their own
 * "not found"); this page only ever sees paths no route claims.
 */
import {
  ArrowLeft,
  Bot,
  Briefcase,
  Bug,
  Building2,
  CalendarDays,
  CheckSquare,
  Compass,
  Handshake,
  LayoutDashboard,
  MessagesSquare,
  Users,
  Video,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { openBugReport } from '../components/bugReports/BugReportWidget';
import type { PermissionAction, PermissionModule } from '../lib/permissionsApi';

interface Section {
  /** First path segment(s) that belong to this section. */
  prefixes: string[];
  to: string;
  labelKey: string;
  icon: LucideIcon;
  gate: [PermissionModule, PermissionAction] | null;
}

// Order matters twice: it is the order of the shortcut row, and the first
// prefix match wins when the URL is read.
const SECTIONS: Section[] = [
  {
    prefixes: ['dashboard', 'home'],
    to: '/dashboard',
    labelKey: 'sidebar.dashboard',
    icon: LayoutDashboard,
    gate: ['dashboard', 'canView'],
  },
  {
    prefixes: ['tasks', 'personal-tasks', 'reports'],
    to: '/tasks',
    labelKey: 'sidebar.tasks',
    icon: CheckSquare,
    gate: ['tasks', 'canView'],
  },
  {
    prefixes: ['projects'],
    to: '/projects',
    labelKey: 'sidebar.projects',
    icon: Briefcase,
    gate: ['projects', 'canView'],
  },
  {
    prefixes: ['clients'],
    to: '/clients',
    labelKey: 'sidebar.clients',
    icon: Building2,
    gate: ['clients', 'canView'],
  },
  {
    prefixes: ['leads'],
    to: '/leads',
    labelKey: 'sidebar.leads',
    icon: Handshake,
    gate: ['leads', 'canView'],
  },
  {
    prefixes: ['financials', 'invoices', 'smartbill', 'subscriptions', 'suppliers'],
    to: '/financials',
    labelKey: 'sidebar.financials',
    icon: Wallet,
    gate: ['financials', 'canView'],
  },
  {
    prefixes: ['meetings', 'calendar'],
    to: '/meetings',
    labelKey: 'sidebar.meetings',
    icon: CalendarDays,
    gate: ['meetings', 'canView'],
  },
  {
    prefixes: ['meet'],
    to: '/meet',
    labelKey: 'sidebar.meet',
    icon: Video,
    gate: ['videoMeetings', 'canView'],
  },
  {
    prefixes: ['team-chat', 'voice'],
    to: '/team-chat',
    labelKey: 'sidebar.teamChat',
    icon: MessagesSquare,
    gate: null,
  },
  {
    prefixes: ['people'],
    to: '/people',
    labelKey: 'sidebar.people',
    icon: Users,
    gate: ['people', 'canView'],
  },
  {
    prefixes: ['chat'],
    to: '/chat',
    labelKey: 'sidebar.chat',
    icon: Bot,
    gate: ['aiAssistant', 'canAccess'],
  },
];

/** The shortcut row shows these, in this order — the places people go most. */
const SHORTCUTS = ['/dashboard', '/tasks', '/projects', '/clients', '/financials', '/chat'];

export function NotFoundPage() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const allowed = useMemo(
    () => SECTIONS.filter((s) => s.gate === null || can(s.gate[0], s.gate[1])),
    [can],
  );
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? '';
  const guessed = allowed.find((s) => s.prefixes.includes(firstSegment)) ?? null;
  const shortcuts = SHORTCUTS.map((to) => allowed.find((s) => s.to === to)).filter(
    (s): s is Section => Boolean(s) && s !== guessed,
  );
  // Going "back" only makes sense when there is a Firmio page behind us; a
  // link opened in a fresh tab has nothing there, and the fallback is home.
  const canGoBack = window.history.length > 1;

  return (
    <div className="flex min-h-full flex-1 items-center justify-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-2xl text-center">
        {/* Big, unmistakable 404 with a soft glow and the compass riding on it. */}
        <div className="relative mx-auto flex w-fit items-center justify-center">
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 scale-125 rounded-full bg-primary/20 blur-3xl"
          />
          <span
            aria-hidden="true"
            className="select-none bg-gradient-to-br from-primary-300 via-primary-500 to-primary-700 bg-clip-text text-[7rem] font-black leading-none tracking-tighter text-transparent sm:text-[10rem]"
          >
            404
          </span>
          <span className="absolute -right-4 -top-2 flex h-14 w-14 animate-bounce items-center justify-center rounded-2xl border border-border bg-card text-primary shadow-lg [animation-duration:2.4s] sm:-right-6 sm:h-16 sm:w-16">
            <Compass size={30} aria-hidden="true" />
          </span>
        </div>

        <h1 className="mt-6 text-2xl font-bold text-foreground sm:text-3xl">
          {t('notFound.title')}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
          {guessed
            ? t('notFound.sectionHint', { section: t(guessed.labelKey) })
            : t('notFound.genericHint')}
        </p>
        <p
          className="mx-auto mt-4 max-w-md truncate rounded-full border border-border bg-card px-4 py-1.5 font-mono text-xs text-muted-foreground"
          title={pathname}
        >
          {pathname}
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {guessed && (
            <Link
              to={guessed.to}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition hover:-translate-y-0.5 hover:bg-primary/90"
            >
              <guessed.icon size={18} aria-hidden="true" />
              {t('notFound.goTo', { section: t(guessed.labelKey) })}
            </Link>
          )}
          <button
            type="button"
            onClick={() => (canGoBack ? navigate(-1) : navigate('/'))}
            className={[
              'inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition hover:-translate-y-0.5',
              guessed
                ? 'border border-border bg-card text-foreground hover:bg-accent'
                : 'bg-primary text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90',
            ].join(' ')}
          >
            {canGoBack ? (
              <ArrowLeft size={18} aria-hidden="true" />
            ) : (
              <LayoutDashboard size={18} aria-hidden="true" />
            )}
            {canGoBack ? t('notFound.back') : t('sidebar.dashboard')}
          </button>
        </div>

        {shortcuts.length > 0 && (
          <div className="mt-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('notFound.elsewhere')}
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {shortcuts.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="group flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-sm font-medium text-foreground transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition group-hover:bg-primary/20">
                      <s.icon size={18} aria-hidden="true" />
                    </span>
                    <span className="truncate">{t(s.labelKey)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10 flex flex-col items-center gap-2 text-xs text-muted-foreground sm:flex-row sm:justify-center">
          <span>{t('notFound.footer')}</span>
          <button
            type="button"
            onClick={openBugReport}
            className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
          >
            <Bug size={14} aria-hidden="true" />
            {t('bugReport.widget.open')}
          </button>
        </div>
      </div>
    </div>
  );
}
